import React, { useState } from 'react';
import { Popover, Switch } from '@mui/material';
import { ReactComponent as LayersIcon } from 'new/components/icons/fi-rr-layers.svg';
// Equalizer/sliders icon (prototype) for the pagination settings affordance.
import { ReactComponent as SettingsIcon } from 'new/components/icons/fi-rr-sliders.svg';
import { PaginationConfig } from './PaginationConfig';
import { getIterationTypeDef } from './iterationFields';
import classes from './StepPaginationControl.module.scss';

/**
 * Redesigned per-step pagination control (prototype's "Paginate" pill).
 *
 * Mirrors the prototype StepPagination: a compact pill with a Paginate switch,
 * a ×N "stop after" badge, and a gear button that opens a popover containing the
 * full settings (rendered by PaginationConfig with its own toggle hidden).
 *
 * USABILITY-ONLY: every change is forwarded via `onChange(key, value)` which the
 * caller wires to the existing `onAPIConfigChanged(key, value, stageIndex)`, so the
 * produced config is byte-identical to the legacy inline pagination block (which
 * RestApiStepList suppresses via ConfigureAPIStage's `hidePaginationSection`).
 */

export interface StepPaginationControlProps {
  stageIndex: number;
  /** the iteration object: apiConfig['rest.iterations'][stageIndex] */
  stageConfig: Record<string, any>;
  /** this step's probe response, used to validate path params in the settings */
  apiResponse?: any;
  /** caller forwards to onAPIConfigChanged(key, value, stageIndex) */
  onChange: (key: string, value: any) => void;
}

/** The per-type "stop after" key (end.page.to / end.offset.to / end.id.to / …). */
const getStopAfterValue = (stageConfig: Record<string, any>): string | number | undefined => {
  const def = getIterationTypeDef(stageConfig['iteration.type']);
  const endField = def?.fields.find((f) => /^end\./.test(f.key));
  const value = endField ? stageConfig[endField.key] : undefined;
  return value === '' || value == null ? undefined : value;
};

export const StepPaginationControl: React.FC<StepPaginationControlProps> = ({
  stageIndex,
  stageConfig,
  apiResponse,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isPaginated: boolean = !!stageConfig.isPaginated;
  const stopAfter = getStopAfterValue(stageConfig);

  const handleTogglePaginated = (checked: boolean) => {
    // Legacy only toggles isPaginated; "not paginated ⇒ static.url" is applied at
    // serialize time. Firing a second onAPIConfigChanged here raced with this one
    // and reverted the toggle, so we intentionally do NOT reset iteration.type.
    onChange('isPaginated', checked);
    if (!checked) {
      setAnchorEl(null);
    }
  };

  return (
    <div
      className={classes.pill}
      data-testid={`step-pagination-${stageIndex}`}
      onClick={(e) => e.stopPropagation()}
    >
      <LayersIcon className={classes.layersIcon} aria-hidden="true" />
      <span className={classes.label}>Paginate</span>
      <Switch
        size="small"
        checked={isPaginated}
        onChange={(e) => handleTogglePaginated(e.target.checked)}
        data-testid={`step-pagination-toggle-${stageIndex}`}
        inputProps={{ 'aria-label': 'Paginate this step' } as any}
      />

      {isPaginated && stopAfter != null && (
        <span className={classes.countBadge} data-testid={`step-pagination-badge-${stageIndex}`}>
          ×{stopAfter}
        </span>
      )}

      {isPaginated && (
        <>
          <button
            type="button"
            className={classes.gearButton}
            aria-label="Pagination settings"
            data-testid={`step-pagination-gear-${stageIndex}`}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            <SettingsIcon className={classes.gearIcon} />
          </button>
          <Popover
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { className: classes.popoverPaper } }}
          >
            <div
              className={classes.popoverBody}
              data-testid={`step-pagination-popover-${stageIndex}`}
            >
              <div className={classes.popoverHeader}>
                <SettingsIcon className={classes.popoverHeaderIcon} aria-hidden="true" />
                <span className={classes.popoverTitle}>Pagination Settings</span>
              </div>
              <PaginationConfig
                hideToggle
                stageIndex={stageIndex}
                stageConfig={stageConfig}
                apiResponse={apiResponse}
                onChange={onChange}
              />
            </div>
          </Popover>
        </>
      )}
    </div>
  );
};

export default StepPaginationControl;
